import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Bike, Car, Truck, Trash2, Plus, Save, RefreshCw, Settings, 
  Clock, Calendar, Package, ArrowRight, ArrowLeftRight,
  ChevronDown, ChevronUp
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const VehicleSettingsPage = () => {
  const [settings, setSettings] = useState([]);
  const [featureControls, setFeatureControls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [showAddSubtype, setShowAddSubtype] = useState(null);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [newSubtype, setNewSubtype] = useState({ name: "", description: "", fare_multiplier: 1.0 });
  const [newVehicle, setNewVehicle] = useState({
    vehicle_type: "",
    display_name: "",
    base_fare: 30,
    per_km_rate: 10,
    per_min_rate: 1,
    min_fare: 40,
    booking_fee: 5,
    waiting_charge: 1,
    cancellation_fee: 15,
    free_waiting_time: 5,
    is_active: true
  });
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehicleRes, controlsRes] = await Promise.all([
        axios.get(`${API}/superadmin/vehicle-settings`),
        axios.get(`${API}/superadmin/feature-controls`)
      ]);
      setSettings(vehicleRes.data);
      setFeatureControls(controlsRes.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load vehicle settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVehicle = async () => {
    if (!editVehicle) return;
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/vehicle-settings/${editVehicle.vehicle_type}`, editVehicle);
      toast.success(`${editVehicle.display_name || editVehicle.vehicle_type} updated`);
      setEditVehicle(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to update vehicle settings");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateVehicle = async () => {
    if (!newVehicle.vehicle_type) {
      toast.error("Vehicle type is required");
      return;
    }
    try {
      await axios.post(`${API}/superadmin/vehicle-settings`, {
        ...newVehicle,
        subtypes: [],
        is_one_way_enabled: true,
        is_two_way_enabled: true,
        is_hourly_enabled: true,
        is_daily_enabled: true,
        is_package_enabled: true,
        hourly_pricing: { base_fare: 50, per_hour_rate: 40, kms_included_per_hour: 10, extra_km_rate: 8 },
        daily_pricing: { base_fare: 300, per_day_rate: 250, kms_included_per_day: 100, extra_km_rate: 7 },
        package_pricing: { base_fare: 30, per_km_rate: 8, max_weight_kg: 10, extra_weight_charge: 5 }
      });
      toast.success(`${newVehicle.display_name || newVehicle.vehicle_type} created`);
      setShowAddVehicle(false);
      setNewVehicle({
        vehicle_type: "", display_name: "", base_fare: 30, per_km_rate: 10, per_min_rate: 1,
        min_fare: 40, booking_fee: 5, waiting_charge: 1, cancellation_fee: 15, free_waiting_time: 5, is_active: true
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create vehicle");
    }
  };

  const handleDeleteVehicle = async (vehicleType) => {
    if (!window.confirm(`Delete ${vehicleType}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/superadmin/vehicle-settings/${vehicleType}`);
      toast.success("Vehicle type deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete vehicle type");
    }
  };

  const handleAddSubtype = async (vehicleType) => {
    if (!newSubtype.name) {
      toast.error("Please enter a subtype name");
      return;
    }
    try {
      await axios.post(`${API}/superadmin/vehicle-settings/${vehicleType}/subtypes`, newSubtype);
      toast.success(`${newSubtype.name} added`);
      setShowAddSubtype(null);
      setNewSubtype({ name: "", description: "", fare_multiplier: 1.0 });
      fetchData();
    } catch (err) {
      toast.error("Failed to add subtype");
    }
  };

  const handleDeleteSubtype = async (vehicleType, subtypeId) => {
    if (!window.confirm("Delete this subtype?")) return;
    try {
      await axios.delete(`${API}/superadmin/vehicle-settings/${vehicleType}/subtypes/${subtypeId}`);
      toast.success("Subtype deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete subtype");
    }
  };

  const handleUpdateFeatureControls = async (key, value) => {
    const updated = { ...featureControls, [key]: value };
    try {
      await axios.put(`${API}/superadmin/feature-controls`, updated);
      setFeatureControls(updated);
      toast.success("Setting updated");
    } catch (err) {
      toast.error("Failed to update setting");
    }
  };

  const toggleSection = (vehicleType, section) => {
    setExpandedSections(prev => ({
      ...prev,
      [`${vehicleType}-${section}`]: !prev[`${vehicleType}-${section}`]
    }));
  };

  const vehicleIcons = { bike: Bike, auto: Car, car: Car, goods_carrier: Truck };
  const vehicleColors = { bike: "bg-blue-500", auto: "bg-green-500", car: "bg-purple-500", goods_carrier: "bg-orange-500" };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="sa-vehicle-settings-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Global Vehicle Settings</h1>
          <p className="text-muted-foreground">
            Manage default vehicle types, pricing, booking types, and subtypes for all admins
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddVehicle(true)} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add Vehicle Type
          </Button>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Feature Controls Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Global Feature Controls
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <h4 className="font-medium">Theme Customization</h4>
                <p className="text-sm text-muted-foreground">
                  Control who can customize themes
                </p>
              </div>
              <Select 
                value={featureControls?.theme_control || "admin"} 
                onValueChange={(v) => handleUpdateFeatureControls("theme_control", v)}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admins Can Customize</SelectItem>
                  <SelectItem value="superadmin">Super Admin Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <h4 className="font-medium">Fare Calculation</h4>
                <p className="text-sm text-muted-foreground">
                  {featureControls?.fare_calculation_source === "superadmin" 
                    ? "Global rates only (no Admin overrides)"
                    : "Hierarchical (Admin can override)"
                  }
                </p>
              </div>
              <Select 
                value={featureControls?.fare_calculation_source || "admin"} 
                onValueChange={(v) => handleUpdateFeatureControls("fare_calculation_source", v)}
              >
                <SelectTrigger className="w-52">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Hierarchical</SelectItem>
                  <SelectItem value="superadmin">Global Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Pricing Hierarchy</h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p><span className="font-medium">1. Super Admin Global</span> - Default rates</p>
              <p><span className="font-medium">2. Admin Override</span> - Override for their network</p>
              <p><span className="font-medium">3. City Admin Override</span> - Override for specific cities</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Settings */}
      <div className="grid gap-6">
        {settings.map((vs) => {
          const Icon = vehicleIcons[vs.vehicle_type] || Car;
          
          return (
            <Card key={vs.vehicle_type}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 ${vehicleColors[vs.vehicle_type] || "bg-gray-500"} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="capitalize flex items-center gap-2">
                      {vs.display_name || vs.vehicle_type}
                      {!vs.is_active && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Inactive</span>}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {vs.subtypes?.length || 0} subtypes
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditVehicle({ ...vs })}>
                    Edit All Settings
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowAddSubtype(vs.vehicle_type)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Subtype
                  </Button>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteVehicle(vs.vehicle_type)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Base Pricing Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Base Fare</p>
                    <p className="font-bold">₹{vs.base_fare}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Per KM</p>
                    <p className="font-bold">₹{vs.per_km_rate}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Per Min</p>
                    <p className="font-bold">₹{vs.per_min_rate}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Min Fare</p>
                    <p className="font-bold">₹{vs.min_fare}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Booking Fee</p>
                    <p className="font-bold">₹{vs.booking_fee}</p>
                  </div>
                </div>

                {/* Booking Types */}
                <div className="flex flex-wrap gap-2">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${vs.is_one_way_enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    <ArrowRight className="w-3 h-3" /> One-Way
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${vs.is_two_way_enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    <ArrowLeftRight className="w-3 h-3" /> Two-Way
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${vs.is_hourly_enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    <Clock className="w-3 h-3" /> Hourly
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${vs.is_daily_enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    <Calendar className="w-3 h-3" /> Daily
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${vs.is_package_enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    <Package className="w-3 h-3" /> Package
                  </div>
                </div>

                {/* Collapsible Sections */}
                <div className="space-y-2">
                  {/* Hourly Pricing */}
                  {vs.is_hourly_enabled && vs.hourly_pricing && (
                    <Collapsible open={expandedSections[`${vs.vehicle_type}-hourly`]} onOpenChange={() => toggleSection(vs.vehicle_type, 'hourly')}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-blue-50 rounded-lg hover:bg-blue-100">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-blue-700">Hourly Pricing</span>
                        </div>
                        {expandedSections[`${vs.vehicle_type}-hourly`] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2 p-3 border rounded-lg">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div><span className="text-muted-foreground">Base:</span> ₹{vs.hourly_pricing.base_fare}</div>
                          <div><span className="text-muted-foreground">Per Hour:</span> ₹{vs.hourly_pricing.per_hour_rate}</div>
                          <div><span className="text-muted-foreground">KMs/Hour:</span> {vs.hourly_pricing.kms_included_per_hour}</div>
                          <div><span className="text-muted-foreground">Extra KM:</span> ₹{vs.hourly_pricing.extra_km_rate}</div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {/* Daily Pricing */}
                  {vs.is_daily_enabled && vs.daily_pricing && (
                    <Collapsible open={expandedSections[`${vs.vehicle_type}-daily`]} onOpenChange={() => toggleSection(vs.vehicle_type, 'daily')}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-purple-50 rounded-lg hover:bg-purple-100">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-purple-600" />
                          <span className="font-medium text-purple-700">Daily Pricing</span>
                        </div>
                        {expandedSections[`${vs.vehicle_type}-daily`] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2 p-3 border rounded-lg">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div><span className="text-muted-foreground">Base:</span> ₹{vs.daily_pricing.base_fare}</div>
                          <div><span className="text-muted-foreground">Per Day:</span> ₹{vs.daily_pricing.per_day_rate}</div>
                          <div><span className="text-muted-foreground">KMs/Day:</span> {vs.daily_pricing.kms_included_per_day}</div>
                          <div><span className="text-muted-foreground">Extra KM:</span> ₹{vs.daily_pricing.extra_km_rate}</div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {/* Package Pricing */}
                  {vs.is_package_enabled && vs.package_pricing && (
                    <Collapsible open={expandedSections[`${vs.vehicle_type}-package`]} onOpenChange={() => toggleSection(vs.vehicle_type, 'package')}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-orange-50 rounded-lg hover:bg-orange-100">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-orange-600" />
                          <span className="font-medium text-orange-700">Package Pricing</span>
                        </div>
                        {expandedSections[`${vs.vehicle_type}-package`] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2 p-3 border rounded-lg">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div><span className="text-muted-foreground">Base:</span> ₹{vs.package_pricing.base_fare}</div>
                          <div><span className="text-muted-foreground">Per KM:</span> ₹{vs.package_pricing.per_km_rate}</div>
                          <div><span className="text-muted-foreground">Max Weight:</span> {vs.package_pricing.max_weight_kg}kg</div>
                          <div><span className="text-muted-foreground">Extra/kg:</span> ₹{vs.package_pricing.extra_weight_charge}</div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>

                {/* Subtypes */}
                {vs.subtypes?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-muted-foreground">Subtypes</h4>
                    <div className="flex flex-wrap gap-2">
                      {vs.subtypes.map((st) => (
                        <div 
                          key={st.id} 
                          className={`px-3 py-2 rounded-lg border flex items-center gap-2 ${
                            st.is_active !== false ? "bg-card" : "bg-muted opacity-60"
                          }`}
                        >
                          <span className="font-medium">{st.display_name || st.name}</span>
                          <span className="text-xs text-muted-foreground">×{st.fare_multiplier || st.multiplier}</span>
                          <button
                            onClick={() => handleDeleteSubtype(vs.vehicle_type, st.id)}
                            className="text-destructive hover:text-destructive/80"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Vehicle Dialog */}
      <Dialog open={!!editVehicle} onOpenChange={() => setEditVehicle(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editVehicle?.display_name || editVehicle?.vehicle_type}</DialogTitle>
          </DialogHeader>
          {editVehicle && (
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Basic</TabsTrigger>
                <TabsTrigger value="hourly">Hourly</TabsTrigger>
                <TabsTrigger value="daily">Daily</TabsTrigger>
                <TabsTrigger value="package">Package</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Display Name</Label>
                    <Input 
                      value={editVehicle.display_name || ""} 
                      onChange={(e) => setEditVehicle({...editVehicle, display_name: e.target.value})}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label>Active</Label>
                      <Switch 
                        checked={editVehicle.is_active} 
                        onCheckedChange={(v) => setEditVehicle({...editVehicle, is_active: v})}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Base Fare (₹)</Label>
                    <Input type="number" value={editVehicle.base_fare} onChange={(e) => setEditVehicle({...editVehicle, base_fare: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Per KM Rate (₹)</Label>
                    <Input type="number" value={editVehicle.per_km_rate} onChange={(e) => setEditVehicle({...editVehicle, per_km_rate: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Per Minute Rate (₹)</Label>
                    <Input type="number" value={editVehicle.per_min_rate} onChange={(e) => setEditVehicle({...editVehicle, per_min_rate: parseFloat(e.target.value) || 0})} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Minimum Fare (₹)</Label>
                    <Input type="number" value={editVehicle.min_fare} onChange={(e) => setEditVehicle({...editVehicle, min_fare: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Booking Fee (₹)</Label>
                    <Input type="number" value={editVehicle.booking_fee} onChange={(e) => setEditVehicle({...editVehicle, booking_fee: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Cancellation Fee (₹)</Label>
                    <Input type="number" value={editVehicle.cancellation_fee} onChange={(e) => setEditVehicle({...editVehicle, cancellation_fee: parseFloat(e.target.value) || 0})} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Waiting Charge (₹/min)</Label>
                    <Input type="number" value={editVehicle.waiting_charge} onChange={(e) => setEditVehicle({...editVehicle, waiting_charge: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Free Waiting (mins)</Label>
                    <Input type="number" value={editVehicle.free_waiting_time} onChange={(e) => setEditVehicle({...editVehicle, free_waiting_time: parseInt(e.target.value) || 0})} />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-base font-semibold mb-3 block">Booking Types</Label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm">One-Way</span>
                      <Switch checked={editVehicle.is_one_way_enabled !== false} onCheckedChange={(v) => setEditVehicle({...editVehicle, is_one_way_enabled: v})} />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm">Two-Way</span>
                      <Switch checked={editVehicle.is_two_way_enabled !== false} onCheckedChange={(v) => setEditVehicle({...editVehicle, is_two_way_enabled: v})} />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm">Hourly</span>
                      <Switch checked={editVehicle.is_hourly_enabled !== false} onCheckedChange={(v) => setEditVehicle({...editVehicle, is_hourly_enabled: v})} />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm">Daily</span>
                      <Switch checked={editVehicle.is_daily_enabled !== false} onCheckedChange={(v) => setEditVehicle({...editVehicle, is_daily_enabled: v})} />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm">Package</span>
                      <Switch checked={editVehicle.is_package_enabled !== false} onCheckedChange={(v) => setEditVehicle({...editVehicle, is_package_enabled: v})} />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="hourly" className="space-y-4 pt-4">
                <div className="p-4 bg-blue-50 rounded-lg mb-4">
                  <h4 className="font-medium text-blue-800 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Hourly Booking Settings
                  </h4>
                  <p className="text-sm text-blue-600">Customer pays for time with included kilometers</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Base Fare (₹)</Label>
                    <Input type="number" value={editVehicle.hourly_pricing?.base_fare || 0} onChange={(e) => setEditVehicle({...editVehicle, hourly_pricing: {...(editVehicle.hourly_pricing || {}), base_fare: parseFloat(e.target.value) || 0}})} />
                  </div>
                  <div>
                    <Label>Per Hour Rate (₹)</Label>
                    <Input type="number" value={editVehicle.hourly_pricing?.per_hour_rate || 0} onChange={(e) => setEditVehicle({...editVehicle, hourly_pricing: {...(editVehicle.hourly_pricing || {}), per_hour_rate: parseFloat(e.target.value) || 0}})} />
                  </div>
                  <div>
                    <Label>KMs Included Per Hour</Label>
                    <Input type="number" value={editVehicle.hourly_pricing?.kms_included_per_hour || 0} onChange={(e) => setEditVehicle({...editVehicle, hourly_pricing: {...(editVehicle.hourly_pricing || {}), kms_included_per_hour: parseInt(e.target.value) || 0}})} />
                  </div>
                  <div>
                    <Label>Extra KM Rate (₹)</Label>
                    <Input type="number" value={editVehicle.hourly_pricing?.extra_km_rate || 0} onChange={(e) => setEditVehicle({...editVehicle, hourly_pricing: {...(editVehicle.hourly_pricing || {}), extra_km_rate: parseFloat(e.target.value) || 0}})} />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="daily" className="space-y-4 pt-4">
                <div className="p-4 bg-purple-50 rounded-lg mb-4">
                  <h4 className="font-medium text-purple-800 flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Daily Booking Settings
                  </h4>
                  <p className="text-sm text-purple-600">Customer pays for full day rental with included kilometers</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Base Fare (₹)</Label>
                    <Input type="number" value={editVehicle.daily_pricing?.base_fare || 0} onChange={(e) => setEditVehicle({...editVehicle, daily_pricing: {...(editVehicle.daily_pricing || {}), base_fare: parseFloat(e.target.value) || 0}})} />
                  </div>
                  <div>
                    <Label>Per Day Rate (₹)</Label>
                    <Input type="number" value={editVehicle.daily_pricing?.per_day_rate || 0} onChange={(e) => setEditVehicle({...editVehicle, daily_pricing: {...(editVehicle.daily_pricing || {}), per_day_rate: parseFloat(e.target.value) || 0}})} />
                  </div>
                  <div>
                    <Label>KMs Included Per Day</Label>
                    <Input type="number" value={editVehicle.daily_pricing?.kms_included_per_day || 0} onChange={(e) => setEditVehicle({...editVehicle, daily_pricing: {...(editVehicle.daily_pricing || {}), kms_included_per_day: parseInt(e.target.value) || 0}})} />
                  </div>
                  <div>
                    <Label>Extra KM Rate (₹)</Label>
                    <Input type="number" value={editVehicle.daily_pricing?.extra_km_rate || 0} onChange={(e) => setEditVehicle({...editVehicle, daily_pricing: {...(editVehicle.daily_pricing || {}), extra_km_rate: parseFloat(e.target.value) || 0}})} />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="package" className="space-y-4 pt-4">
                <div className="p-4 bg-orange-50 rounded-lg mb-4">
                  <h4 className="font-medium text-orange-800 flex items-center gap-2">
                    <Package className="w-4 h-4" /> Package Delivery Settings
                  </h4>
                  <p className="text-sm text-orange-600">For parcel/package delivery with weight-based pricing</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Base Fare (₹)</Label>
                    <Input type="number" value={editVehicle.package_pricing?.base_fare || 0} onChange={(e) => setEditVehicle({...editVehicle, package_pricing: {...(editVehicle.package_pricing || {}), base_fare: parseFloat(e.target.value) || 0}})} />
                  </div>
                  <div>
                    <Label>Per KM Rate (₹)</Label>
                    <Input type="number" value={editVehicle.package_pricing?.per_km_rate || 0} onChange={(e) => setEditVehicle({...editVehicle, package_pricing: {...(editVehicle.package_pricing || {}), per_km_rate: parseFloat(e.target.value) || 0}})} />
                  </div>
                  <div>
                    <Label>Max Weight Included (kg)</Label>
                    <Input type="number" value={editVehicle.package_pricing?.max_weight_kg || 0} onChange={(e) => setEditVehicle({...editVehicle, package_pricing: {...(editVehicle.package_pricing || {}), max_weight_kg: parseInt(e.target.value) || 0}})} />
                  </div>
                  <div>
                    <Label>Extra Weight Charge (₹/kg)</Label>
                    <Input type="number" value={editVehicle.package_pricing?.extra_weight_charge || 0} onChange={(e) => setEditVehicle({...editVehicle, package_pricing: {...(editVehicle.package_pricing || {}), extra_weight_charge: parseFloat(e.target.value) || 0}})} />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVehicle(null)}>Cancel</Button>
            <Button onClick={handleSaveVehicle} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Subtype Dialog */}
      <Dialog open={!!showAddSubtype} onOpenChange={() => setShowAddSubtype(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Subtype to {showAddSubtype}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Name</Label>
              <Input 
                value={newSubtype.name} 
                onChange={(e) => setNewSubtype({...newSubtype, name: e.target.value})}
                placeholder="e.g., Premium, Economy"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={newSubtype.description} 
                onChange={(e) => setNewSubtype({...newSubtype, description: e.target.value})}
                placeholder="e.g., Comfortable AC ride"
              />
            </div>
            <div>
              <Label>Fare Multiplier</Label>
              <Input 
                type="number" 
                step="0.1"
                value={newSubtype.fare_multiplier} 
                onChange={(e) => setNewSubtype({...newSubtype, fare_multiplier: parseFloat(e.target.value) || 1})}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Base fare × {newSubtype.fare_multiplier} = Subtype fare
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSubtype(null)}>Cancel</Button>
            <Button onClick={() => handleAddSubtype(showAddSubtype)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Subtype
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Vehicle Type Dialog */}
      <Dialog open={showAddVehicle} onOpenChange={setShowAddVehicle}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Vehicle Type</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicle Type (ID)</Label>
                <Input 
                  value={newVehicle.vehicle_type} 
                  onChange={(e) => setNewVehicle({...newVehicle, vehicle_type: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  placeholder="e.g., truck, van"
                />
              </div>
              <div>
                <Label>Display Name</Label>
                <Input 
                  value={newVehicle.display_name} 
                  onChange={(e) => setNewVehicle({...newVehicle, display_name: e.target.value})}
                  placeholder="e.g., Delivery Truck"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Base Fare (₹)</Label>
                <Input type="number" value={newVehicle.base_fare} onChange={(e) => setNewVehicle({...newVehicle, base_fare: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Per KM (₹)</Label>
                <Input type="number" value={newVehicle.per_km_rate} onChange={(e) => setNewVehicle({...newVehicle, per_km_rate: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Min Fare (₹)</Label>
                <Input type="number" value={newVehicle.min_fare} onChange={(e) => setNewVehicle({...newVehicle, min_fare: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddVehicle(false)}>Cancel</Button>
            <Button onClick={handleCreateVehicle}>
              <Plus className="w-4 h-4 mr-2" />
              Create Vehicle Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleSettingsPage;
